(() => {
  if (window.__thmDeliveryEstimatorInitialized) return;
  window.__thmDeliveryEstimatorInitialized = true;

  const ATTRIBUTE_KEY = 'Delivery area';
  const LAHORE = 'Lahore';
  const OUTSIDE_LAHORE = 'Outside Lahore';
  const FREE_DELIVERY_THRESHOLD = 350000;
  const DELIVERY_PRICES = {
    [LAHORE]: 30000,
    [OUTSIDE_LAHORE]: 40000,
  };

  const ROOT = window.Shopify?.routes?.root || '/';
  const CART_URL = `${ROOT}cart.js`;
  const UPDATE_URL = `${ROOT}cart/update.js`;

  let cartState = null;
  let currentArea = LAHORE;
  let refreshTimer = null;
  let requestSequence = 0;
  let redirectingToCheckout = false;

  const normalizeArea = (value) => (value === OUTSIDE_LAHORE ? OUTSIDE_LAHORE : LAHORE);

  const formatMoney = (minorUnits) => {
    const amount = (Number(minorUnits) || 0) / 100;
    return `Rs.${amount.toLocaleString('en-PK', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} PKR`;
  };

  const getDeliveryEstimate = (subtotal) => {
    if (subtotal >= FREE_DELIVERY_THRESHOLD) return 0;
    return DELIVERY_PRICES[currentArea];
  };

  function assignIndependentRadioGroups() {
    document.querySelectorAll('[data-thm-delivery-selector]').forEach((selector, index) => {
      if (!(selector instanceof HTMLElement)) return;
      const groupName = `thm-delivery-area-${index + 1}`;
      selector.querySelectorAll('[data-thm-delivery-option]').forEach((input) => {
        if (input instanceof HTMLInputElement) input.name = groupName;
      });
    });
  }

  function createSummaryRow(kind, labelText) {
    const row = document.createElement('span');
    row.className = 'cart-totals__item thm-delivery-summary';
    row.dataset.thmDeliverySummary = kind;

    const label = document.createElement('span');
    label.className = 'cart-primary-typography';
    label.textContent = labelText;

    const value = document.createElement('span');
    value.className = 'cart-secondary-typography';
    value.dataset.thmDeliverySummaryValue = '';

    row.append(label, value);
    return row;
  }

  function ensureSummaryRows(selector, subtotal) {
    const summary =
      selector.closest('.cart-drawer__summary, .cart-summary__inner') || selector.parentElement;
    if (!(summary instanceof HTMLElement)) return;

    const container = summary.querySelector('.cart-totals__container');
    if (!(container instanceof HTMLElement)) return;

    const subtotalRow = container.querySelector('.cart-totals__total');
    const subtotalLabel = subtotalRow?.querySelector('.cart-totals__total-label');
    if (subtotalLabel instanceof HTMLElement) subtotalLabel.textContent = 'Items subtotal';

    let deliveryRow = container.querySelector('[data-thm-delivery-summary="delivery"]');
    if (!(deliveryRow instanceof HTMLElement)) {
      deliveryRow = createSummaryRow('delivery', 'Delivery estimate');
      subtotalRow?.insertAdjacentElement('afterend', deliveryRow);
    }

    let totalRow = container.querySelector('[data-thm-delivery-summary="total"]');
    if (!(totalRow instanceof HTMLElement)) {
      totalRow = createSummaryRow('total', 'Estimated total with delivery');
      deliveryRow.insertAdjacentElement('afterend', totalRow);
      totalRow.setAttribute('aria-live', 'polite');
    }

    const delivery = getDeliveryEstimate(subtotal);
    const deliveryValue = deliveryRow.querySelector('[data-thm-delivery-summary-value]');
    const totalValue = totalRow.querySelector('[data-thm-delivery-summary-value]');

    if (deliveryValue instanceof HTMLElement) {
      deliveryValue.textContent = delivery === 0 ? 'Free' : formatMoney(delivery);
    }
    if (totalValue instanceof HTMLElement) totalValue.textContent = formatMoney(subtotal + delivery);
  }

  function render() {
    const subtotal = Number(cartState?.total_price) || 0;
    assignIndependentRadioGroups();

    document.querySelectorAll('[data-thm-delivery-selector]').forEach((selector) => {
      if (!(selector instanceof HTMLElement)) return;

      selector.querySelectorAll('[data-thm-delivery-option]').forEach((input) => {
        if (!(input instanceof HTMLInputElement)) return;
        const selected = input.value === currentArea;
        input.checked = selected;
        input.closest('.thm-delivery-area__option')?.classList.toggle('is-selected', selected);
      });

      ensureSummaryRows(selector, subtotal);
    });
  }

  async function fetchCart() {
    const sequence = ++requestSequence;

    try {
      const response = await fetch(CART_URL, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;

      const cart = await response.json();
      if (sequence !== requestSequence) return;

      cartState = cart;
      currentArea = normalizeArea(cart.attributes?.[ATTRIBUTE_KEY]);
      render();
    } catch (error) {
      console.warn('Could not refresh the delivery estimate.', error);
    }
  }

  async function saveArea(area) {
    currentArea = normalizeArea(area);
    render();

    try {
      const response = await fetch(UPDATE_URL, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ attributes: { [ATTRIBUTE_KEY]: currentArea } }),
      });

      if (response.ok) {
        cartState = await response.json();
        currentArea = normalizeArea(cartState.attributes?.[ATTRIBUTE_KEY]);
      }
    } catch (error) {
      console.warn('Could not save the delivery-area selection.', error);
    }

    render();
    return currentArea;
  }

  function scheduleRefresh(delay = 500) {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(fetchCart, delay);
  }

  function getSelectedAreaFromPage() {
    const checkedInputs = [...document.querySelectorAll('[data-thm-delivery-option]:checked')];
    const visibleInput = checkedInputs.find(
      (input) => input instanceof HTMLInputElement && input.getClientRects().length > 0
    );

    return visibleInput instanceof HTMLInputElement
      ? normalizeArea(visibleInput.value)
      : currentArea;
  }

  function buildCheckoutUrl(area) {
    const checkoutUrl = new URL(`${ROOT}cart/checkout`, window.location.origin);
    const pageUrl = new URL(window.location.href);
    const previewThemeId = pageUrl.searchParams.get('preview_theme_id');

    if (previewThemeId) checkoutUrl.searchParams.set('preview_theme_id', previewThemeId);

    checkoutUrl.searchParams.set('checkout[shipping_address][country]', 'Pakistan');

    if (area === LAHORE) {
      checkoutUrl.searchParams.set('checkout[shipping_address][city]', 'Lahore');
    } else {
      checkoutUrl.searchParams.set('checkout[shipping_address][city]', '');
      checkoutUrl.searchParams.set('checkout[shipping_address][address1]', '');
      checkoutUrl.searchParams.set('checkout[shipping_address][address2]', '');
      checkoutUrl.searchParams.set('checkout[shipping_address][province]', '');
      checkoutUrl.searchParams.set('checkout[shipping_address][zip]', '');
    }

    return checkoutUrl.toString();
  }

  async function proceedToCheckout() {
    if (redirectingToCheckout) return;
    redirectingToCheckout = true;

    const selectedArea = getSelectedAreaFromPage();
    const confirmedArea = await saveArea(selectedArea);

    window.location.assign(buildCheckoutUrl(confirmedArea));
  }

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (input instanceof HTMLInputElement && input.matches('[data-thm-delivery-option]')) {
      void saveArea(input.value);
      return;
    }

    if (input instanceof Element && input.closest('cart-items-component')) scheduleRefresh(700);
  });

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const checkoutButton = target.closest('button[name="checkout"], input[name="checkout"]');
      if (checkoutButton instanceof HTMLElement) {
        event.preventDefault();
        event.stopImmediatePropagation();
        void proceedToCheckout();
        return;
      }

      if (target.closest('[data-testid="cart-drawer-trigger"]')) scheduleRefresh(150);
      if (target.closest('cart-items-component')) scheduleRefresh(700);
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const submitter = event.submitter;
      if (!(submitter instanceof HTMLElement) || submitter.getAttribute('name') !== 'checkout') return;

      event.preventDefault();
      event.stopImmediatePropagation();
      void proceedToCheckout();
    },
    true
  );

  ['cart:update', 'cart:refresh', 'cart:updated', 'cart:change', 'shopify:section:load'].forEach(
    (eventName) => document.addEventListener(eventName, () => scheduleRefresh(100))
  );

  window.addEventListener('pageshow', () => {
    redirectingToCheckout = false;
    scheduleRefresh(0);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fetchCart, { once: true });
  } else {
    void fetchCart();
  }
})();
